import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';

import { PAYMENT_PROVIDERS_NAMES, PaymentProviderStatuses } from '@/domain/paySettings/helpers';
import type { PaymentProviderData } from '@/domain/paySettings/helpers';

// A payment account as GET payment-accounts lists it and POST payment-accounts (connect) returns it (DPM-846).
// `providerName` is any string, so an entry of a removed provider parses and is then dropped by name instead of
// failing the whole list. `status` is any string too and is normalised below.
export const PaymentProviderResponseSchema = z.object({
  providerName: z.string(),
  status: z.string().optional(),
  id: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
});
export const PaymentProvidersResponseSchema = z.array(PaymentProviderResponseSchema);
export type PaymentProviderResponse = z.infer<typeof PaymentProviderResponseSchema>;

// Any other status (e.g. a leftover `kyc_pending`) is not usable: treated as disconnected, so it opens neither the
// workspace nor Request Access.
const PaymentProviderStatusSchema = z
  .enum([PaymentProviderStatuses.connected, PaymentProviderStatuses.disconnected])
  .catch(PaymentProviderStatuses.disconnected);

const isOpenFinance = ({ providerName }: PaymentProviderResponse) =>
  providerName === PAYMENT_PROVIDERS_NAMES.openFinance;

const toOpenFinanceProvider = ({ status, id, displayName }: PaymentProviderResponse): PaymentProviderData => ({
  providerName: PAYMENT_PROVIDERS_NAMES.openFinance,
  status: PaymentProviderStatusSchema.parse(status),
  id: id ?? null,
  displayName: displayName ?? '',
});

// GET payment-accounts. DOKKA Pay supports Open Finance only; other providers the backend may return are dropped.
export const parseOpenFinanceProviders = (data: unknown): PaymentProviderData[] =>
  pipe(PaymentProvidersResponseSchema.parse(data ?? []), A.filter(isOpenFinance), A.map(toOpenFinanceProvider));

// POST payment-accounts: the provider just connected, normalised like a list entry. Only Open Finance can be
// connected, so any other provider in the response is an error.
export const parseConnectedOpenFinanceProvider = (data: unknown): PaymentProviderData =>
  toOpenFinanceProvider(
    PaymentProviderResponseSchema.refine(isOpenFinance, {
      message: `Expected the ${PAYMENT_PROVIDERS_NAMES.openFinance} provider`,
    }).parse(data),
  );

// GET payment-accounts/{account_id}/funds/paying-accounts/form (DPM-781).
// Bank codes are not unique (`010` = Leumi and Pepper) — the bank is identified by
// its option `value`.

// `catchall`: options carry extra attributes (`bank_code`, `logo_url`, parent value).
export const PayingAccountFieldOptionSchema = z.object({ label: z.string(), value: z.string() }).catchall(z.string());
export type PayingAccountFieldOption = z.infer<typeof PayingAccountFieldOptionSchema>;

// Extend together with the renderer; fields of any other type are dropped.
export const PayingAccountFieldTypeSchema = z.enum(['text', 'select', 'autocomplete', 'iban']);

const hasRenderableType = (field: { type: string }) => PayingAccountFieldTypeSchema.safeParse(field.type).success;

export const PayingAccountFieldRawSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    type: PayingAccountFieldTypeSchema,
    // A flat list, or a map keyed by the parent's value (branch_code: { masad: [...] }).
    options: z
      .union([z.array(PayingAccountFieldOptionSchema), z.record(z.string(), z.array(PayingAccountFieldOptionSchema))])
      .optional(),
    dependsOn: z.array(z.string()).optional(),
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
    readOnly: z.boolean().optional(),
    hidden: z.boolean().optional(),
    searchable: z.boolean().optional(),
    computedFrom: z.array(z.string()).optional(),
  })
  .passthrough();

type RawPayingAccountField = z.infer<typeof PayingAccountFieldRawSchema>;
type OptionMap = Record<string, PayingAccountFieldOption[]>;

const isOptionMap = (options: RawPayingAccountField['options']): options is OptionMap =>
  !!options && !Array.isArray(options);

// Flattens a per-parent option map, tagging each option with its parent value.
const flattenParentScopedOptions = (field: RawPayingAccountField) =>
  pipe(
    Option.all([
      pipe(Option.fromNullable(field.options), Option.filter(isOptionMap)),
      Option.fromNullable(field.dependsOn?.[0]),
    ]),
    Option.map(([map, parentName]) => ({
      ...field,
      options: pipe(
        Object.entries(map),
        A.flatMap(([parentValue, options]) =>
          pipe(
            options,
            A.map((option) => ({ ...option, [parentName]: parentValue })),
          ),
        ),
      ),
    })),
    Option.getOrElse(() => ({
      ...field,
      options: Array.isArray(field.options) ? field.options : undefined,
    })),
  );

export const PayingAccountFieldSchema = PayingAccountFieldRawSchema.transform(flattenParentScopedOptions);
export type PayingAccountField = z.output<typeof PayingAccountFieldSchema>;

export const PayingAccountFormSchema = z
  .object({
    formTitle: z.string(),
    formSubTitle: z.string().optional(),
    fields: z
      .array(z.looseObject({ type: z.string() }))
      .transform(A.filter(hasRenderableType))
      .pipe(z.array(PayingAccountFieldSchema)),
  })
  .passthrough();
export type PayingAccountForm = z.output<typeof PayingAccountFormSchema>;

export const PaymentAccountIdPathSchema = z.object({ accountId: z.string().min(1) });

// A row of GET .../funds/paying-accounts; prefills the edit form.
export const PayingAccountRowSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    accountNumber: z.string().optional(),
    psuId: z.string().optional(),
    bank: z.string().optional(),
  })
  .passthrough();
export type PayingAccountRow = z.output<typeof PayingAccountRowSchema>;
